import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi, githubAuthApiRef, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { TabProps, TenantConfig, TenantConfiguratorVariant, BusinessUnit } from './types';
import { computeExpiresDate } from './defaults';
import { fetchModuleVersions, ModuleVersionInfo } from '../services/registryClient';
import { parseModuleSource } from '../services/moduleSourceParser';

const MODULE_SOURCE = 'app.terraform.io/Badal_devex/create-tenant/platform';

function parseMajor(constraint: string): number | undefined {
  const m = constraint.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : undefined;
}

interface GeneralTabProps extends TabProps {
  variant: TenantConfiguratorVariant;
  businessUnits: BusinessUnit[];
  onLifetimeBudgetOverride: () => void;
  lifetimeBudgetOverridden: boolean;
  buLocked?: boolean;
}

const TENANT_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

export function GeneralTab({
  config,
  onChange,
  variant,
  businessUnits,
  onLifetimeBudgetOverride,
  lifetimeBudgetOverridden,
  buLocked,
}: GeneralTabProps) {
  const [nameError, setNameError] = useState<string | null>(null);

  // Registry-driven major-version picker for the create-tenant module
  const githubAuth = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');
  const sourceInfo = useMemo(() => parseModuleSource(MODULE_SOURCE), []);
  const [versions, setVersions] = useState<ModuleVersionInfo[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await githubAuth.getAccessToken(['repo']);
        const result = await fetchModuleVersions(sourceInfo, {
          githubToken: token,
          fetchApi,
          backendUrl,
        });
        setVersions(result.versions);
      } catch {
        // Non-critical — fall back to whatever default is in config
      }
    };
    load();
  }, [githubAuth, fetchApi, sourceInfo, backendUrl]);

  const majorVersions = useMemo(() => {
    const set = new Set<number>();
    for (const v of versions) {
      if (/[-+]/.test(v.version)) continue; // skip pre-releases
      const m = v.version.replace(/^v/, '').match(/^(\d+)/);
      if (m) set.add(parseInt(m[1], 10));
    }
    return [...set].sort((a, b) => b - a); // newest first
  }, [versions]);

  const currentMajor = parseMajor(config.module_version);
  const majorsToShow = majorVersions.length > 0
    ? majorVersions
    : (currentMajor !== undefined ? [currentMajor] : []);

  // Once we know the latest major from the registry, auto-default to it for
  // create flows. Edit mode is excluded so loading an old tenant (e.g. ~> 1.5.0)
  // doesn't silently bump it to a new major.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (variant === 'edit') return;
    if (defaultedRef.current) return;
    if (majorVersions.length === 0) return;
    defaultedRef.current = true;
    const latestMajor = majorVersions[0];
    if (currentMajor !== latestMajor) {
      onChange({ ...config, module_version: `~> ${latestMajor}.0` });
    }
  }, [majorVersions, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMajorChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const major = e.target.value as number;
    onChange({ ...config, module_version: `~> ${major}.0` });
  };

  const maxMonths = variant === 'sandbox' ? 6 : 12;
  const monthOptions = Array.from({ length: maxMonths }, (_, i) => i + 1);

  const selectableUnits = businessUnits.filter(bu => bu.repo !== null);
  const selectedBu = businessUnits.find(bu => bu.repo === config.business_unit_repo) || null;

  const handleBuChange = (_: unknown, value: BusinessUnit | null) => {
    onChange({ ...config, business_unit_repo: value?.repo ?? '' });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (name && !TENANT_NAME_PATTERN.test(name)) {
      setNameError(
        'Must start with a lowercase letter and contain only lowercase letters, numbers, and hyphens',
      );
    } else {
      setNameError(null);
    }
    onChange({ ...config, tenant_name: name });
  };

  const handleMonthsChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const months = e.target.value as number;
    const expiresDate = computeExpiresDate(months);
    const updated: TenantConfig = {
      ...config,
      project_end_months: months,
      project_end_date: expiresDate,
      project_expiry: expiresDate,
    };
    updated.topics = config.topics.filter(t => !t.toLowerCase().startsWith('expires-'));
    updated.labels = config.labels.filter(l => l.key !== 'expires');
    onChange(updated);
  };

  // Edit mode: direct date input
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value; // YYYY-MM-DD
    if (dateStr) {
      onChange({ ...config, project_end_date: dateStr, project_expiry: dateStr });
    } else if (!hasExistingExpiry) {
      // Only allow clearing if there was no original expiry
      onChange({ ...config, project_end_date: '', project_expiry: '' });
    }
  };

  const displayDate = config.project_end_date ? config.project_end_date.replace(/^expires-/i, '') : '';
  // displayDate handles old EXPIRES- (uppercase), new expires- (lowercase), and plain ISO format
  const showBudget = variant !== 'sandbox';
  const showLifetimeBudget = variant !== 'edit';
  // In edit mode: track if tenant originally had an expiry (can't be removed)
  const initialExpiryRef = useRef<boolean | null>(null);
  if (variant === 'edit' && initialExpiryRef.current === null) {
    initialExpiryRef.current = !!config.project_end_date;
  }
  const hasExistingExpiry = variant === 'edit' && (initialExpiryRef.current ?? false);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
      {/* Business Unit */}
      <Autocomplete
        options={selectableUnits}
        getOptionLabel={(bu) => bu.name}
        getOptionSelected={(option, value) => option.repo === value.repo}
        value={selectedBu}
        onChange={handleBuChange}
        disabled={buLocked}
        renderOption={(bu) => (
          <Box>
            <Typography variant="body2">{bu.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {bu.repo} ({bu.short})
            </Typography>
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Business Unit"
            variant="outlined"
            fullWidth
            required
            error={!config.business_unit_repo}
            helperText={
              buLocked
                ? `Repository: ${selectedBu?.repo} (locked)`
                : !config.business_unit_repo
                  ? 'Required — select a business unit'
                  : selectedBu
                    ? `Repository: ${selectedBu.repo} — Changing this will reset the environments list to the defaults for the selected business unit.`
                    : 'Select a business unit'
            }
          />
        )}
      />

      {/* Tenant Name (not shown in edit mode — name is from the file) */}
      {variant !== 'edit' && (
        <TextField
          label="Tenant Name"
          variant="outlined"
          fullWidth
          required
          value={config.tenant_name}
          onChange={handleNameChange}
          error={!!nameError || !config.tenant_name}
          helperText={
            nameError
              ? nameError
              : !config.tenant_name
                ? 'Lowercase letters, numbers, and hyphens only. Max 25 characters. ex. my-new-tenant'
                : 'Lowercase letters, numbers, and hyphens only'
          }
        />
      )}

      {variant === 'edit' && (
        <TextField
          label="Tenant Name"
          variant="outlined"
          fullWidth
          value={config.tenant_name}
          disabled
          helperText="Loaded from the selected tenant file"
        />
      )}

      <TextField
        label="Description"
        variant="outlined"
        fullWidth
        required
        multiline
        minRows={2}
        maxRows={4}
        value={config.description}
        onChange={e => onChange({ ...config, description: e.target.value })}
        error={!config.description}
        helperText={
          !config.description
            ? 'Required — enter a description for the tenant'
            : 'Description for the tenant repository and Terraform workspaces'
        }
      />

      <TextField
        label="Project Creation Date"
        variant="outlined"
        fullWidth
        value={config.project_creation}
        disabled
        helperText="Automatically set to today's date"
      />

      {/* Project End Date */}
      {variant === 'edit' ? (
        // Edit mode: date picker — can't clear if originally had expiry
        <TextField
          label="Project End Date"
          type="date"
          variant="outlined"
          fullWidth
          value={displayDate || ''}
          onChange={handleDateChange}
          InputLabelProps={{ shrink: true }}
          inputProps={hasExistingExpiry ? { required: true } : {}}
          helperText={
            config.project_end_date
              ? `Expires: ${displayDate}${hasExistingExpiry ? ' (cannot be removed)' : ''}`
              : 'No end date set — select a date to add an expiry, or leave empty'
          }
        />
      ) : (
        // Create modes: months dropdown
        <>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="project-end-months-label">
              Project End Date (months)
            </InputLabel>
            <Select
              labelId="project-end-months-label"
              value={config.project_end_months}
              onChange={handleMonthsChange}
              label="Project End Date (months)"
            >
              {monthOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m} {m === 1 ? 'month' : 'months'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Expires: {displayDate}
          </Typography>
        </>
      )}

      {/* Budget (not shown in sandbox) */}
      {showBudget && (
        <>
          <Divider />
          <Typography variant="subtitle1">Budget</Typography>

          <TextField
            label="Lifetime Budget (USD)"
            variant="outlined"
            fullWidth
            type="number"
            value={config.lifetime_budget}
            onChange={e => {
              const lifetime = Number(e.target.value) || 0;
              const monthly = config.project_end_months > 0
                ? Math.round(lifetime / config.project_end_months)
                : lifetime;
              onChange({
                ...config,
                lifetime_budget: lifetime,
                budget_amount: lifetimeBudgetOverridden ? config.budget_amount : monthly,
              });
            }}
            disabled={variant === 'edit'}
            error={variant !== 'edit' && config.lifetime_budget <= 100}
            helperText={
              variant === 'edit'
                ? 'Loaded from existing tenant configuration'
                : config.lifetime_budget <= 100
                  ? 'Must be greater than $100'
                  : 'Total budget for the project lifetime'
            }
          />

          {showLifetimeBudget && (
            <TextField
              label="Monthly Budget (USD)"
              variant="outlined"
              fullWidth
              type="number"
              value={config.budget_amount}
              onChange={e => {
                onLifetimeBudgetOverride();
                onChange({ ...config, budget_amount: Number(e.target.value) || 0 });
              }}
              error={config.budget_amount <= 10}
              helperText={
                config.budget_amount <= 10
                  ? 'Must be greater than $10'
                  : lifetimeBudgetOverridden
                    ? 'Manually overridden'
                    : `Auto-computed: ${config.lifetime_budget} / ${config.project_end_months} months`
              }
            />
          )}
        </>
      )}

      {/* create-tenant Terraform module version */}
      {majorsToShow.length > 0 && (
        <>
          <Divider />
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="module-version-label">create-tenant Module Version</InputLabel>
            <Select
              labelId="module-version-label"
              value={currentMajor ?? majorsToShow[0]}
              onChange={handleMajorChange}
              label="create-tenant Module Version"
            >
              {majorsToShow.map(m => (
                <MenuItem key={m} value={m}>
                  v{m}.x{majorVersions.length > 0 && m === majorVersions[0] ? ' — latest' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="textSecondary">
            The latest minor/patch within v{currentMajor ?? majorsToShow[0]}.x will be picked up automatically.
          </Typography>
        </>
      )}
    </Box>
  );
}
